import React, { useEffect, useMemo, useState } from 'react';
import { callStatusLabel, isCallConnected } from '@/utilities/callStatus';
import moment from 'moment';
import {
  Bot,
  FileText,
  GitBranch,
  Grid3x3,
  Layers,
  Megaphone,
  Mic2,
  Network,
  PhoneIncoming,
  PhoneOutgoing,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Loader from '@/components/partials/components/Loader';
import CallRecordingCell from '@/components/partials/components/CallRecordingCell';
import { getApiCall } from '@/service/httpCall';
import { CDR_TRACE_REPORT } from '@/service/url';
import { CAMPAIGN_TYPE_MAP, TEMPLATE_TYPE_MAP, USER_ROLES } from '@/utilities/constant';
import { isCallCentreUserRole } from '@/utilities/agentLeadsQuota';
import { getAgentMasking, maskNumberForHangup, maskReportRowNumbers, isChannelLicenceShortageHangup } from '@/utilities/cliDisplay';
import { getUser } from '@/utilities/setting';
import { getRecordingData, hasCdrMediaReference } from '@/utilities/cdrMedia';
import { canReadRecordings, hasVisibleCdrMedia, isVoicemailEntry } from '@/utilities/recordingsAcl';
import { pseudoCdrLabel } from '@/utilities/pseudoCdr';
import { formatDispositionJson, DispositionJsonCell } from '@/features/ivr-reports/DispositionJsonCell';
import { CallTranscriptButton } from '@/features/transcripts/CallTranscript';
import { CallEngineTracesButton } from './CallEngineTraces';
import CallQualityDetail from './CallQualityDetail';
import CallFlowView, { CallLegsTable } from './CallFlowView';
import { buildCallFlow, isAiIvrEventType, parseTime, queueToCallGroup, type Row } from './callFlowModel';
import {
  IvrJourneyPanel,
  WebhooksPanel,
  formatIvrJourneyStep,
  getAiReportMeta,
  getIvrJourneyForCalls,
  getWebhooksForCalls,
  isAgentReportLeg,
  uniqueReportLegs,
} from '../DidReportDetailPage';

export interface CdrCallDetailDialogProps {
  /** The CDR row that was clicked; null closes the dialog. */
  row: Row | null;
  /** Raw DID the row was loaded for (the row's own `did` may be masked). */
  did: string;
  /** CDR rows already on the page — fallback when the trace call fails. */
  pageRows: Row[];
  userRole: string;
  hangupCauseMap: Record<string, string>;
  /** Transcript line count for the trace (0 hides the transcript button). */
  transcriptLines: number;
  onClose: () => void;
}

const has = (v: any) => v !== null && v !== undefined && v !== '';
const legKey = (r: Row) => String(r.call_sid || r.id || `${r.trace_id}|${r.start_time}|${r.call_leg}`);
// A_PARTY also carries agent_* (who it was connected to), so call_leg decides the side.
const isAgentLeg = (r: Row) =>
  r.call_leg ? String(r.call_leg).toUpperCase() !== 'A_PARTY' : isAgentReportLeg(r);
const legName = (r: Row, agentMask = false) =>
  isAgentLeg(r)
    ? r.transfer_to_name || r.agent_name || maskNumberForHangup(r.agent_number, r.hangup_cause, agentMask) || 'Agent'
    : 'Customer';
const rawSecs = (v: any) => `${Number(v) || 0}s`;
const when = (v: any) => {
  const at = parseTime(v);
  return at == null ? '' : moment(at).format('DD MMM YYYY, hh:mm:ss A');
};

/** Recording / voicemail the viewer may actually play, same rule as CallRecordingCell. */
const hasPlayableMedia = (recordingFile: any) => {
  // No Recordings read ACL → no recordings/voicemails in this dialog at all.
  if (!recordingFile || !canReadRecordings()) return false;
  if (hasCdrMediaReference(recordingFile)) return hasVisibleCdrMedia(recordingFile);
  const canRead = canReadRecordings();
  return getRecordingData(recordingFile).some((rec: any) => canRead || isVoicemailEntry(rec));
};

const pill = (ok: boolean, text: string) => (
  <span
    className={
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ' +
      (ok
        ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
        : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
    }
  >
    {text}
  </span>
);

// Stored CDR call_status wins; older rows without it keep the billing / answer_status rule.
const isLegConnected = (r: Row) =>
  isCallConnected(r, Number(r.billing_duration) > 0 || String(r.answer_status || '').toLowerCase() === 'answered');
const legStatusText = (r: Row) => callStatusLabel(r, isLegConnected(r));

type Field = [label: string, value: React.ReactNode];

/** One titled card of label/value rows; empty values are dropped, an empty card is not rendered. */
const Section = ({ title, fields, wide }: { title: string; fields: Field[]; wide?: boolean }) => {
  const shown = fields.filter(([, v]) => has(v) && v !== false);
  if (!shown.length) return null;
  return (
    <section className={'rounded-2xl border border-border/60 bg-card p-4 ' + (wide ? 'md:col-span-2' : '')}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      <dl className="m-0 grid gap-x-4 gap-y-1.5 text-sm [grid-template-columns:minmax(120px,auto)_1fr]">
        {shown.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="m-0 min-w-0 break-words font-medium text-foreground">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
};

export default function CdrCallDetailDialog({
  row,
  did,
  pageRows,
  userRole,
  hangupCauseMap,
  transcriptLines,
  onClose,
}: CdrCallDetailDialogProps) {
  const [legs, setLegs] = useState<Row[]>([]);
  const [hasIncomingLeg, setHasIncomingLeg] = useState(false);
  const [callTimes, setCallTimes] = useState<{ start?: string; end?: string; sec?: number | null; talk?: number | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('flow');
  const [selectedKey, setSelectedKey] = useState('');
  // Agent viewer: number masking backstop (backend masks too); no PBX / pseudo-CDR internals.
  const agentMask = useMemo(() => getAgentMasking(getUser()), []);
  const isAgentViewer = userRole === USER_ROLES.CALL_CENTRE_AGENT;

  useEffect(() => {
    if (!row) return;
    setTab('flow');
    setSelectedKey(legKey(row));
    setCallTimes(null);
    const fallback = () => {
      const sameTrace = row.trace_id ? pageRows.filter((r) => r.trace_id === row.trace_id) : [row];
      setHasIncomingLeg(sameTrace.some((r) => r.call_type === 'incoming'));
      const fallbackLegs = uniqueReportLegs(sameTrace.length ? sameTrace : [row]);
      // Page rows are not media-filtered per agent: an agent viewer gets no media here.
      setLegs(isAgentViewer ? fallbackLegs.map((r: Row) => ({ ...r, recording_file: null })) : fallbackLegs);
    };
    if (!row.trace_id || !did) {
      fallback();
      return;
    }
    let cancelled = false;
    setLoading(true);
    const enc = encodeURIComponent;
    // Same params as the DID summary modal (trace lookup ignores the range; it keys the cache).
    getApiCall(
      `${CDR_TRACE_REPORT}/${enc(did)}/${enc(row.start_time)}/${enc(row.end_time || row.start_time)}/${enc(row.trace_id)}?source=${row.call_type === 'outgoing' ? 'outbound' : 'incoming'}`,
      undefined
    )
      .then((response: any) => {
        if (cancelled) return;
        const incoming = response?.incomingReports || response?.data?.incomingReports || [];
        const outbound = response?.outboundReports || response?.data?.outboundReports || [];
        const all = [...incoming, ...outbound];
        if (!all.length) return fallback();
        const force = all.some((r: Row) => isChannelLicenceShortageHangup(r?.hangup_cause));
        setHasIncomingLeg(incoming.length > 0);
        const t = response?.data ?? response ?? {};
        setCallTimes({ start: t.call_start_time, end: t.call_end_time, sec: t.call_duration_sec, talk: t.call_talk_sec });
        setLegs(uniqueReportLegs(all).map((r: Row) => maskReportRowNumbers(r, { force })));
      })
      .catch(() => !cancelled && fallback())
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [row, did, pageRows, isAgentViewer]);

  const mainCall = useMemo(() => legs.find((r) => !isAgentLeg(r)) || legs[0] || null, [legs]);
  const relatedCalls = useMemo(() => legs.filter((r) => r !== mainCall), [legs, mainCall]);
  const selected = legs.find((r) => legKey(r) === selectedKey) || mainCall;
  const isOutgoing = !hasIncomingLeg;

  const ivrSteps = useMemo(
    () => (mainCall ? getIvrJourneyForCalls(mainCall, relatedCalls).map((e: any) => formatIvrJourneyStep(e, agentMask)) : []),
    [mainCall, relatedCalls, agentMask]
  );
  const webhooks = useMemo(
    () => (mainCall ? getWebhooksForCalls(mainCall, relatedCalls) : []),
    [mainCall, relatedCalls]
  );
  // Agent viewer: the trace endpoint keeps only media whose recordings/voicemails row
  // carries their own agent_id (on any leg; media lands on A_PARTY), and the fallback
  // drops media entirely, so others' media is never listed or fetched.
  const mediaLegs = useMemo(() => legs.filter((r) => hasPlayableMedia(r.recording_file)), [legs]);
  const aiMeta = mainCall ? getAiReportMeta(mainCall, relatedCalls) : null;

  const flow = useMemo(
    () =>
      mainCall
        ? buildCallFlow({
            mainCall,
            relatedCalls,
            did,
            isOutgoing,
            hangupLabel: (code: any) => (code ? hangupCauseMap[code] || '' : ''),
            mask: (value: any) => maskNumberForHangup(value ?? '', mainCall.hangup_cause, agentMask) || '',
            ivrHasAi: ivrSteps.some((step: any) => isAiIvrEventType(step.type)),
          })
        : null,
    [mainCall, relatedCalls, did, isOutgoing, hangupCauseMap, ivrSteps, agentMask]
  );

  const num = (r: Row, key: string) => maskNumberForHangup(r[key] ?? '', r.hangup_cause, agentMask) || '';
  const hangup = (code: any) => (has(code) ? `${hangupCauseMap[code] || 'Unknown'} (${code})` : '');
  const campaignType = has(mainCall?.campaign_type) ? (CAMPAIGN_TYPE_MAP as Record<string, string>)[String(mainCall?.campaign_type)] : '';
  const templateName = has(mainCall?.template_id)
    ? (TEMPLATE_TYPE_MAP as Record<string, string>)[String(mainCall?.template_id)] || `#${mainCall?.template_id}`
    : '';

  const chipCls = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ';
  const highlights = mainCall ? (
    <>
      {mediaLegs.length ? (
        <button
          type="button"
          onClick={() => setTab('media')}
          className={chipCls + 'cursor-pointer border-0 bg-emerald-100 text-emerald-800 ring-emerald-300 hover:bg-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300'}
        >
          <Mic2 size={12} /> Recording available
        </button>
      ) : null}
      {mainCall.campaign_name || mainCall.campaign_id ? (
        <span className={chipCls + 'bg-sky-100 text-sky-800 ring-sky-300 dark:bg-sky-950/50 dark:text-sky-300'}>
          <Megaphone size={12} /> {mainCall.campaign_name || `Campaign #${mainCall.campaign_id}`}
        </span>
      ) : null}
      {aiMeta?.aiNotes?.sentiment ? (
        <span className={chipCls + 'bg-violet-100 text-violet-800 ring-violet-300 dark:bg-violet-950/50 dark:text-violet-300'}>
          <Bot size={12} /> Sentiment: {String(aiMeta.aiNotes.sentiment)}
        </span>
      ) : null}
    </>
  ) : null;

  const connected = mainCall ? legs.some(isLegConnected) : false;

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogTitle className="sr-only">Call details</DialogTitle>
        {loading || !mainCall ? (
          <Loader />
        ) : (
          <div>
            {/* Header: who called whom, direction, outcome, ids */}
            <div className="mb-4 pr-8">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Call Information
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 text-xl font-bold tabular-nums text-foreground">
                  {num(mainCall, 'caller_number') || '-'} → {num(mainCall, 'callee_number') || '-'}
                </h3>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {isOutgoing ? <PhoneOutgoing size={12} /> : <PhoneIncoming size={12} />}
                  {isOutgoing ? 'Outgoing' : 'Incoming'}
                </span>
                {pill(connected, connected ? 'Connected' : callStatusLabel(mainCall, false))}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  Trace ID: <span className="font-mono">{mainCall.trace_id || '-'}</span>
                </span>
                <span>·</span>
                <span>
                  {when(callTimes?.start ?? mainCall.start_time) || '-'}
                  {callTimes?.end ? ` – ${when(callTimes.end)}` : ''}
                </span>
                {callTimes?.sec != null ? (
                  <>
                    <span>·</span>
                    <span>Duration: {new Date(callTimes.sec * 1000).toISOString().slice(11, 19)}</span>
                    {callTimes.talk != null ? (
                      <>
                        <span>·</span>
                        <span>Customer Talk: {new Date(callTimes.talk * 1000).toISOString().slice(11, 19)}</span>
                      </>
                    ) : null}
                  </>
                ) : null}
                <CallTranscriptButton traceId={mainCall.trace_id} lineCount={transcriptLines} className="h-7 w-7" />
                {userRole === USER_ROLES.SUPERADMIN ? (
                  <CallEngineTracesButton callSid={mainCall.call_sid} sipCallId={mainCall.sip_call_id} className="h-7 w-7" />
                ) : null}
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
                <TabsTrigger value="flow" className="gap-1.5 px-3 py-1.5"><GitBranch size={14} /> Call Flow</TabsTrigger>
                <TabsTrigger value="details" className="gap-1.5 px-3 py-1.5"><FileText size={14} /> Details</TabsTrigger>
                <TabsTrigger value="legs" className="gap-1.5 px-3 py-1.5"><Layers size={14} /> Call Legs ({legs.length})</TabsTrigger>
                {ivrSteps.length ? (
                  <TabsTrigger value="ivr" className="gap-1.5 px-3 py-1.5"><Grid3x3 size={14} /> IVR Journey ({ivrSteps.length})</TabsTrigger>
                ) : null}
                {webhooks.length ? (
                  <TabsTrigger value="webhooks" className="gap-1.5 px-3 py-1.5"><Network size={14} /> Webhooks ({webhooks.length})</TabsTrigger>
                ) : null}
                <TabsTrigger value="media" className="gap-1.5 px-3 py-1.5"><Mic2 size={14} /> Recordings & Quality</TabsTrigger>
              </TabsList>

              <TabsContent value="flow">
                {flow ? (
                  <CallFlowView
                    flow={flow}
                    isOutgoing={isOutgoing}
                    onViewIvrJourney={ivrSteps.length ? () => setTab('ivr') : undefined}
                    extraHighlights={highlights}
                    hideCredits={isCallCentreUserRole(userRole)}
                  >
                    {aiMeta?.aiNotes ? (
                      <section className="rounded-2xl border border-border/60 bg-card p-4">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          AI Call Notes
                          {aiMeta.aiAgentId ? <span className="ml-2 normal-case">Agent #{aiMeta.aiAgentId}</span> : null}
                        </div>
                        <div className="space-y-2 text-sm">
                          {aiMeta.aiNotes.sentiment ? (
                            <div>
                              <span className="font-semibold text-muted-foreground">Sentiment: </span>
                              {String(aiMeta.aiNotes.sentiment)}
                              {aiMeta.aiNotes.sentiment_reason ? ` — ${aiMeta.aiNotes.sentiment_reason}` : ''}
                            </div>
                          ) : null}
                          {aiMeta.aiNotes.needs_human != null ? (
                            <div>
                              <span className="font-semibold text-muted-foreground">Needs human: </span>
                              {aiMeta.aiNotes.needs_human ? 'Yes' : 'No'}
                            </div>
                          ) : null}
                          {aiMeta.aiNotes.brief ? (
                            <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/40 p-3 text-xs leading-relaxed text-foreground">
                              {String(aiMeta.aiNotes.brief)}
                            </pre>
                          ) : null}
                        </div>
                      </section>
                    ) : null}
                  </CallFlowView>
                ) : null}
              </TabsContent>

              <TabsContent value="details">
                {/* Pick the leg; the cards below describe that leg, campaign is per call. */}
                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-xs font-semibold text-muted-foreground">Leg:</span>
                  {legs.map((r) => (
                    <button
                      key={legKey(r)}
                      type="button"
                      onClick={() => setSelectedKey(legKey(r))}
                      className={
                        'rounded-full border px-3 py-1 text-xs font-semibold transition-colors ' +
                        (r === selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-card text-foreground hover:bg-muted')
                      }
                    >
                      {legName(r, agentMask)}
                    </button>
                  ))}
                </div>
                {selected ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <Section
                      title="Outcome"
                      fields={[
                        ['Status', pill(isLegConnected(selected), legStatusText(selected))],
                        ['Answer status', selected.answer_status],
                        ['Hangup', hangup(selected.hangup_cause)],
                        ['Failure reason', selected.call_failure_reason],
                        ['Pseudo CDR', isAgentViewer ? '' : pseudoCdrLabel(selected)],
                        ['DND', selected.dnd ? 'Yes' : ''],
                      ]}
                    />
                    <Section
                      title="Parties"
                      fields={[
                        ['Caller', num(selected, 'caller_number')],
                        ['Callee', num(selected, 'callee_number')],
                        ['DID', num(selected, 'did') || (isOutgoing ? '' : num(mainCall, 'callee_number'))],
                        ['Agent', [selected.agent_name, num(selected, 'agent_number')].filter(Boolean).join(' · ')],
                        ['Transferred by', selected.transferred_by],
                        ['Transferred to', selected.transfer_to_name],
                        ['Username', selected.username],
                      ]}
                    />
                    <Section
                      title="Timing"
                      fields={[
                        ['Start', when(selected.start_time)],
                        ['Answered', when(selected.bridge_time)],
                        ['End', when(selected.end_time)],
                        // Stored seconds as-is.
                        ['Ring', rawSecs(selected.bridge_ring_duration)],
                        ['Talk Time', rawSecs(selected.patch_duration)],
                        ['Total hold', rawSecs(selected.total_hold_duration)],
                      ]}
                    />
                    <Section
                      title="Routing"
                      fields={[
                        ['Routing', has(selected.routing_type) ? `${queueToCallGroup(selected.routing_type)}${has(selected.routing_id) ? ` #${selected.routing_id}` : ''}` : ''],
                        ['IVR', selected.ivr_name || (Number(selected.ivr_id) ? selected.ivr_id : '')],
                        ['Call Group', selected.queue_name || selected.call_group_name],
                        ['DTMF', selected.dtmf],
                        ['Valid DTMF', selected.valid_dtmf],
                        ['PBX', isAgentViewer ? '' : selected.pbx_name],
                        ['Trunk', selected.trunk_type],
                      ]}
                    />
                    <Section
                      title="Campaign"
                      fields={[
                        ['Campaign', mainCall.campaign_name],
                        ['Campaign ID', has(mainCall.campaign_id) ? `#${mainCall.campaign_id}` : ''],
                        ['Campaign type', campaignType || mainCall.campaign_type],
                        ['Template', templateName],
                      ]}
                    />
                    <Section
                      title="Network"
                      fields={[
                        ['Circle series', selected.circle_series],
                        ['Circle', selected.circle_series_name],
                        ['Operator', selected.circle_series_operator_name],
                      ]}
                    />
                    <Section
                      title="Identifiers"
                      wide
                      fields={[
                        ['Call SID', <span className="font-mono text-xs">{selected.call_sid}</span>],
                        ['SIP Call-ID', selected.sip_call_id ? <span className="font-mono text-xs">{selected.sip_call_id}</span> : ''],
                        ['Trace ID', <span className="font-mono text-xs">{selected.trace_id}</span>],
                        ['User ID', selected.user_id],
                        ['Agent ID', selected.agent_id],
                      ]}
                    />
                    <Section
                      title="Disposition"
                      wide
                      fields={[
                        ['Disposition', formatDispositionJson(selected.disposition) ? <DispositionJsonCell value={selected.disposition} /> : ''],
                      ]}
                    />
                  </div>
                ) : null}
              </TabsContent>

              <TabsContent value="legs">
                {flow ? (
                  <CallLegsTable
                    billing={isAgentViewer ? { ...flow.billing, pbxName: '' } : flow.billing}
                    hideBilling={isCallCentreUserRole(userRole)}
                  />
                ) : null}
              </TabsContent>

              {ivrSteps.length ? (
                <TabsContent value="ivr">
                  <IvrJourneyPanel mainCall={mainCall} steps={ivrSteps} agentMask={agentMask} />
                </TabsContent>
              ) : null}
              {webhooks.length ? (
                <TabsContent value="webhooks">
                  <WebhooksPanel events={webhooks} />
                </TabsContent>
              ) : null}

              <TabsContent value="media">
                <div className="space-y-3">
                  {mediaLegs.length ? (
                    mediaLegs.map((r) => (
                      <section key={legKey(r)} className="rounded-2xl border border-border/60 bg-card p-4">
                        <div className="mb-2 text-sm font-bold text-foreground">
                          {legName(r, agentMask)} <span className="font-normal text-muted-foreground">· {when(r.start_time)}</span>
                        </div>
                        <CallRecordingCell recordingFile={r.recording_file} compact={false} />
                      </section>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                      {isAgentViewer ? 'No recording or voicemail of yours for this call.' : 'No recording or voicemail for this call.'}
                    </div>
                  )}
                  <CallQualityDetail
                    callSid={mainCall.call_sid}
                    altIds={[mainCall.trace_id, mainCall.channel_id, mainCall.agent_channel_id, mainCall.call_id, ...relatedCalls.map((c) => c.call_sid)]}
                    showEmptyState={String(mainCall.trunk_type || '').toUpperCase() === 'WEBRTC'}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
